import { FFmpeg } from '@ffmpeg/ffmpeg';
import type { VideoClip, Filter } from '../types';

/**
 * Progress callback type
 */
export type ProgressCallback = (progress: number, message: string) => void;

/**
 * FFmpeg Video Processor
 */
export class FFmpegProcessor {
    private ffmpeg: FFmpeg | null = null;
    private loaded: boolean = false;
    private progressCallback: ProgressCallback | null = null;

    /**
     * Load FFmpeg.wasm
     */
    async load(onProgress?: ProgressCallback): Promise<void> {
        if (this.loaded) return;

        try {
            this.ffmpeg = new FFmpeg();
            this.progressCallback = onProgress || null;

            // Set up logging
            this.ffmpeg.on('log', ({ message }) => {
                console.log('FFmpeg:', message);
            });

            // Set up progress tracking
            this.ffmpeg.on('progress', ({ progress, time }) => {
                const percentage = progress * 100;
                this.progressCallback?.(percentage, `Processing: ${time.toFixed(2)}s`);
            });

            console.log('FFmpeg: Loading from CDN');
            onProgress?.(30, 'Indlæser FFmpeg...');

            // Use default CDN loading - let FFmpeg handle it
            await this.ffmpeg.load();

            this.loaded = true;
            console.log('FFmpeg loaded successfully');
            onProgress?.(100, 'FFmpeg klar!');
        } catch (error) {
            console.error('Failed to load FFmpeg:', error);
            onProgress?.(-1, `Fejl: ${error instanceof Error ? error.message : String(error)}`);
            throw error;
        }
    }

    /**
     * Trim a video clip
     */
    async trimVideo(videoFile: File, startTime: number, endTime: number): Promise<Blob> {
        await this.load();
        if (!this.ffmpeg) throw new Error('FFmpeg not loaded');

        try {
            const inputName = 'input.mp4';
            const outputName = 'output.mp4';

            // Write input file to FFmpeg filesystem
            const arrayBuffer = await videoFile.arrayBuffer();
            await this.ffmpeg.writeFile(inputName, new Uint8Array(arrayBuffer));

            // Calculate duration
            const duration = endTime - startTime;

            // Execute FFmpeg command to trim
            await this.ffmpeg.exec([
                '-i', inputName,
                '-ss', startTime.toString(),
                '-t', duration.toString(),
                '-c', 'copy',
                outputName
            ]);

            // Read output file
            const data = await this.ffmpeg.readFile(outputName) as Uint8Array;

            // Clean up
            await this.ffmpeg.deleteFile(inputName);
            await this.ffmpeg.deleteFile(outputName);

            // Convert to Blob
            return new Blob([new Uint8Array(data)], { type: 'video/mp4' });
        } catch (error) {
            console.error('Trim video error:', error);
            throw error;
        }
    }

    /**
     * Concatenate multiple video clips with transitions
     */
    async concatenateVideos(
        clips: VideoClip[],
        // transitions: Transition[],
        filter: Filter,
        musicFile: File | null = null,
        onProgress?: ProgressCallback
    ): Promise<Blob> {
        // Show loading progress first
        onProgress?.(0, 'Indlæser FFmpeg (32 MB)...');
        await this.load(onProgress);
        if (!this.ffmpeg) throw new Error('FFmpeg not loaded');

        try {
            onProgress?.(0, 'Forbereder videoklip...');

            // Process each clip
            const processedClips: string[] = [];
            for (let i = 0; i < clips.length; i++) {
                const clip = clips[i];
                const inputName = `input_${i}.mp4`;
                const processedName = `processed_${i}.mp4`;

                onProgress?.((i / clips.length) * 30, `Behandler klip ${i + 1}/${clips.length}...`);

                // Write clip to filesystem
                const arrayBuffer = await clip.file.arrayBuffer();
                await this.ffmpeg.writeFile(inputName, new Uint8Array(arrayBuffer));

                // Build filter chain
                const filterChain: string[] = [];

                // Apply color filter if specified
                if (filter && filter.ffmpegFilter) {
                    filterChain.push(filter.ffmpegFilter);
                }

                // Scale to consistent resolution
                filterChain.push('scale=1920:1080:force_original_aspect_ratio=decrease,pad=1920:1080:(ow-iw)/2:(oh-ih)/2');

                // Trim if needed
                const trimArgs: string[] = [];
                if (clip.startTime !== undefined && clip.endTime !== undefined) {
                    trimArgs.push('-ss', clip.startTime.toString());
                    trimArgs.push('-t', (clip.endTime - clip.startTime).toString());
                }

                // Process clip
                await this.ffmpeg.exec([
                    '-i', inputName,
                    ...trimArgs,
                    '-vf', filterChain.join(','),
                    '-c:v', 'libx264',
                    '-preset', 'fast',
                    '-crf', '23',
                    '-c:a', 'aac',
                    '-b:a', '128k',
                    processedName
                ]);

                processedClips.push(processedName);

                // Clean up input
                await this.ffmpeg.deleteFile(inputName);
            }

            onProgress?.(40, 'Sammensætter videoer med transitions...');

            // Create concat file for simple concatenation
            const concatList = processedClips.map(name => `file '${name}'`).join('\n');
            await this.ffmpeg.writeFile('concat.txt', new TextEncoder().encode(concatList));

            const outputName = 'concatenated.mp4';

            // Concatenate videos
            await this.ffmpeg.exec([
                '-f', 'concat',
                '-safe', '0',
                '-i', 'concat.txt',
                '-c', 'copy',
                outputName
            ]);

            onProgress?.(70, 'Tilføjer baggrundsmusik...');

            let finalOutputName = outputName;

            // Add background music if provided
            if (musicFile) {
                const musicName = 'music.mp3';
                const withMusicName = 'final_with_music.mp4';

                // Write music file
                const musicBuffer = await musicFile.arrayBuffer();
                await this.ffmpeg.writeFile(musicName, new Uint8Array(musicBuffer));

                // Mix audio
                await this.ffmpeg.exec([
                    '-i', outputName,
                    '-i', musicName,
                    '-filter_complex', '[0:a][1:a]amix=inputs=2:duration=first:dropout_transition=2[aout]',
                    '-map', '0:v',
                    '-map', '[aout]',
                    '-c:v', 'copy',
                    '-c:a', 'aac',
                    '-b:a', '192k',
                    withMusicName
                ]);

                finalOutputName = withMusicName;

                // Clean up
                await this.ffmpeg.deleteFile(musicName);
            }

            onProgress?.(90, 'Færdiggør video...');

            // Read final output
            const data = await this.ffmpeg.readFile(finalOutputName) as Uint8Array;

            // Clean up all files
            await this.ffmpeg.deleteFile('concat.txt');
            for (const clipName of processedClips) {
                await this.ffmpeg.deleteFile(clipName);
            }
            await this.ffmpeg.deleteFile(outputName);
            if (musicFile) {
                await this.ffmpeg.deleteFile(finalOutputName);
            }

            onProgress?.(100, 'Færdig!');

            // Convert to Blob
            return new Blob([new Uint8Array(data)], { type: 'video/mp4' });
        } catch (error) {
            console.error('Concatenate videos error:', error);
            throw error;
        }
    }

    /**
     * Extract audio from video
     */
    async extractAudio(videoFile: File): Promise<Blob> {
        await this.load();
        if (!this.ffmpeg) throw new Error('FFmpeg not loaded');

        try {
            const inputName = 'input.mp4';
            const outputName = 'audio.mp3';

            const arrayBuffer = await videoFile.arrayBuffer();
            await this.ffmpeg.writeFile(inputName, new Uint8Array(arrayBuffer));

            await this.ffmpeg.exec([
                '-i', inputName,
                '-vn',
                '-acodec', 'libmp3lame',
                '-q:a', '2',
                outputName
            ]);

            const data = await this.ffmpeg.readFile(outputName) as Uint8Array;

            await this.ffmpeg.deleteFile(inputName);
            await this.ffmpeg.deleteFile(outputName);

            return new Blob([new Uint8Array(data)], { type: 'audio/mp3' });
        } catch (error) {
            console.error('Extract audio error:', error);
            throw error;
        }
    }

    /**
     * Get video duration
     */
    async getVideoDuration(videoFile: File): Promise<number> {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                resolve(video.duration);
                URL.revokeObjectURL(video.src);
            };

            video.onerror = reject;
            video.src = URL.createObjectURL(videoFile);
        });
    }

    /**
     * Download video file
     */
    downloadVideo(blob: Blob, filename: string = 'real_estate_video.mp4'): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Check if FFmpeg is loaded
     */
    isLoaded(): boolean {
        return this.loaded;
    }
}

// Export singleton instance
export const ffmpegProcessor = new FFmpegProcessor();
